// tslint:disable:max-line-length
import { Injectable } from '@angular/core';
import { combineLatest } from 'rxjs';
import { MadLibHelpers } from '../../../../ng2/shared/services/mad-lib-services/mad-lib-helpers';

@Injectable()
export class MadLibStateResolver {
  resolveMadLibState($stateParams, students$, madlibOptions$, currentSchool, user, madlibConfig): Promise<any> {
    let madLibState;
    const hasDimensionParams = madlibConfig.hasDimensionParams($stateParams);

    const madLibState$ = combineLatest(students$, madlibOptions$, (students: any, madlibOptions: any) => {
      // get the dimensions based on the listType and the user/userRole
      const dimensions = this.getMadLibDimensions(madlibConfig);
      const madlibOptionsForUser = this.getMadlibOptionsForSchool(madlibOptions, students, currentSchool);

      const madLibStateParams = {
        madlibOptionsForUser,
        currentSchool,
        dimensions,
        $stateParams,
        students$,
        madlibConfig,
      };

      if (hasDimensionParams) madLibState = this.getMadlibStateFromUrl(madLibStateParams);
      if (!hasDimensionParams) madLibState = this.getMadlibStateDefaults(madLibStateParams);
      return madLibState;
    });
    return new Promise(resolve => resolve(madLibState$));
  }

  /**
   * use case: when a user is visting a list with a shared/bookmarked url
   * returns: an array where the object at each index is the dropdown options seen in the ui
   */
  initializeMadLibFromUrl(schoolType: string, dimensions, params, madlibConfig) {
    const [dimension1, dimension2, groupings = null] = dimensions;
    const { Dimension1: dimension1FromUrl, Dimension2: dimension2FromUrl } = params;
    const dimensionsAreIndependent = madlibConfig.dimensionsAreIndependent;
    // take the selection `string` from the url and get the object containing the schoolLevel
    const { schoolLevel: dim1SchoolLevel } = MadLibHelpers.prototype.findNestedOption(
      dimension1.options,
      dimension1FromUrl,
    );
    // get valid dim2 options based on dim1 selection
    const dimension2Options = dimension2.options[dim1SchoolLevel];
    // if no valid options exist based on dim1 selection, default.
    if (!dimension2Options && !dimensionsAreIndependent)
      return this.initializeDependentMadlibsToDefaults(schoolType, dimensions);
    if (!dimension2Options && dimensionsAreIndependent) return this.initializeIndependentMadlibsToDefaults(dimensions);
    if (groupings) {
      // get valid grouping options based on dim2 selection, default.
      const groupingOptions = groupings.options[dimension2FromUrl];
      if (!groupingOptions && !dimensionsAreIndependent)
        return this.initializeDependentMadlibsToDefaults(schoolType, dimensions);
      if (!groupingOptions && dimensionsAreIndependent) return this.initializeIndependentMadlibsToDefaults(dimensions);
      return [
        dimension1,
        { ...dimension2, ...{ options: dimension2Options } },
        { ...groupings, ...{ options: groupingOptions } },
      ];
    } else {
      return [dimension1, { ...dimension2, ...{ options: dimension2Options } }];
    }
  }

  /**
   * use case: when a user is visting a list, transitioning from another list in the portal
   * returns: an array where the object at each index is the dropdown options seen in the ui
   */
  initializeDependentMadlibsToDefaults(schoolType, dimensions) {
    const [dimension1, dimension2, groupings = null] = dimensions;
    // dim1 selection impacts dim2 dropdown options
    const dimension1Default = dimension1.options[0].schoolLevel;
    const dimension2Options = dimension2.options[dimension1Default];
    // dim2 slection impacts groupings dropdown options
    const dimension2Default = dimension2.options[dimension1Default][0].key;
    // create madlib with options
    if (groupings) {
      const groupingOptions = groupings.options[dimension2Default];
      return [
        dimension1,
        { ...dimension2, ...{ options: dimension2Options } },
        { ...groupings, ...{ options: groupingOptions } },
      ];
    } else {
      return [dimension1, { ...dimension2, ...{ options: dimension2Options } }];
    }
  }

  // consider removing this function as it is an identify function and not necessary(JJ)
  initializeIndependentMadlibsToDefaults(dimensions) {
    return dimensions;
  }

  /**
   * use case: when a user is visting a list, transitioning from another list in the portal
   * returns: an object with the currently selected values in the madlib options in the ui based on the parsed url
   */
  initializeMadLibModelToDefaults(madLib) {
    const model = madLib.reduce((model, { madLib, options }) => {
      model[madLib] = options[0];
      return model;
    }, {});
    return model;
  }

  /**
   * use case: when a user is visting a list with a shared/bookmarked url
   * returns: an object with the currently selected values in the madlib options in the ui based on the parsed url
   */
  initializeMadLibModelFromUrl(lib: any[], params) {
    const [dim1, dim2, grouping] = lib;
    const dimension1Options = dim1 ? dim1.options : [];
    const dimension2Options = dim2 ? dim2.options : [];
    const groupingsOptions = grouping ? grouping.options : [];

    const { Dimension1, Dimension2, Groupings } = params;

    const selection1 = MadLibHelpers.prototype.findNestedOption(dimension1Options, Dimension1);
    const selection2 = MadLibHelpers.prototype.findNestedOption(dimension2Options, Dimension2);
    const groupingsSelection = MadLibHelpers.prototype.findNestedOption(groupingsOptions, Groupings);

    const model = {
      Dimension1: selection1 ? selection1 : dimension1Options[0],
      Dimension2: selection2 ? selection2 : dimension2Options[0],
      Groupings: groupingsSelection ? groupingsSelection : groupingsOptions[0],
    };

    return model;
  }

  // returns an array, value @ each index rep a dimesion pill + dropdwn options
  getMadLibDimensions(madlibConfig) {
    const dimension1 = madlibConfig.DIMENSION_1;
    const dimension2 = madlibConfig.DIMENSION_2;

    const dimensions = [dimension1, dimension2, madlibConfig.GROUPING];
    return dimensions;
  }

  /** use case: if a school is a hybrid school, we need to create special madlib options */
  getMadlibOptionsForSchool(madlibOptions, students, currentSchool) {
    let madlibOptionsForSchool = madlibOptions;
    const { schoolType } = currentSchool;
    // default options -- if school is not a hybrid type

    const isHybridSchool = schoolType === '6 to 12' || schoolType === 'K to 8';
    if (isHybridSchool) {
      madlibOptionsForSchool = MadLibHelpers.prototype.createHybridMadlibOptions(
        schoolType,
        students,
        madlibOptionsForSchool,
      );
    }
    return madlibOptionsForSchool;
  }

  // use case: a user is coming to the ui with specified url, ie: refresh or shared url
  getMadlibStateFromUrl(data) {
    const { madlibOptionsForUser, currentSchool, dimensions, $stateParams, madlibConfig } = data;

    const [dimension1, dimension2, groupings] = dimensions;
    const schoolOptions = MadLibHelpers.prototype.getDimensionOptionsForSchoolType(
      currentSchool.schoolType,
      dimension1,
    );
    const dimensionOneForSchool = MadLibHelpers.prototype.generateDimensionForSchool(schoolOptions, dimension1);
    const updatedDimensions = [dimensionOneForSchool, dimension2, groupings];

    const filteredDim1MadLibs = MadLibHelpers.prototype.filterMadLib(
      updatedDimensions,
      madlibOptionsForUser,
      currentSchool,
      madlibConfig.listType,
    );

    const filteredMadLib = this.initializeMadLibFromUrl(
      currentSchool.schoolType,
      filteredDim1MadLibs,
      $stateParams,
      madlibConfig,
    );
    const madLibModel = this.initializeMadLibModelFromUrl(filteredMadLib, $stateParams);
    return { madLibModel, filteredMadLib };
  }

  // use case: a user is coming to the ui with no specified url, likely clicking list option
  getMadlibStateDefaults(data) {
    const { madlibOptionsForUser, currentSchool, dimensions, madlibConfig } = data;

    const [dimension1, dimension2, groupings] = dimensions;
    const schoolOptions = MadLibHelpers.prototype.getDimensionOptionsForSchoolType(
      currentSchool.schoolType,
      dimension1,
    );
    const dimensionOneForSchool = MadLibHelpers.prototype.generateDimensionForSchool(schoolOptions, dimension1);
    const updatedDimensions = [dimensionOneForSchool, dimension2, groupings];

    // dimension1 is populated based on user and schoolType, this takes place in the filter functions
    // these functions populate the child options available in the dim1 dropdown
    const madLibWithFilteredDimension1 = MadLibHelpers.prototype.filterMadLib(
      updatedDimensions,
      madlibOptionsForUser,
      currentSchool,
      madlibConfig.listType,
    );

    const filteredMadLib = !madlibConfig.dimensionsAreIndependent
      ? this.initializeDependentMadlibsToDefaults(currentSchool.schoolType, madLibWithFilteredDimension1)
      : this.initializeIndependentMadlibsToDefaults(madLibWithFilteredDimension1);

    // set the madlib model
    const madLibModel = this.initializeMadLibModelToDefaults(filteredMadLib);
    return { madLibModel, filteredMadLib };
  }
}
